import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Coins, Eye, EyeSlash, Trash, Copy, PencilSimple, Check, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useT } from "@/i18n";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { copyToClipboard } from "@/lib/clipboard";
import { Logo } from "@/components/Logo";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";

export default function UserSettings() {
  const t = useT();
  const nav = useNavigate();
  const { user, refresh, logout } = useAuth();
  // note: user-settings i18n keys use the acct.* namespace
  const [oldPw, setOldPw] = useState("");
  const [newPw, setNewPw] = useState("");
  const [showOldPw, setShowOldPw] = useState(false);
  const [showNewPw, setShowNewPw] = useState(false);
  const [confirmPw, setConfirmPw] = useState("");
  const [showConfirmPw, setShowConfirmPw] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameInput, setNameInput] = useState("");
  const [antiPhish, setAntiPhish] = useState("");
  const [savedAntiPhish, setSavedAntiPhish] = useState("");
  const [txs, setTxs] = useState([]);
  const [delOpen, setDelOpen] = useState(false);
  const [delText, setDelText] = useState("");
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    setSavedAntiPhish(user?.anti_phishing_code || "");
    api.get("/auth/me/transactions").then(({ data }) => setTxs(data)).catch(() => {});
  }, [user]);

  const changePw = async () => {
    try {
      await api.patch("/auth/me/password", { old_password: oldPw, new_password: newPw });
      toast.success(t("acct.pwChanged"));
      setOldPw(""); setNewPw(""); setConfirmPw("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || t("acct.error")); }
  };

  const saveName = async () => {
    const n = nameInput.trim();
    if (!n) return;
    try {
      await api.patch("/auth/me/name", { name: n });
      toast.success(t("acct.nameSaved"));
      setEditingName(false);
      refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || t("acct.error")); }
  };

  const saveAntiPhish = async () => {
    try {
      await api.patch("/auth/me/anti-phishing-code", { code: antiPhish });
      toast.success(t("acct.antiPhishSaved"));
      setSavedAntiPhish(antiPhish.trim());
      setAntiPhish("");
      refresh();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || t("acct.error")); }
  };

  const money = (amount, cur) => `${(amount / 100).toFixed(2)} ${(cur || "eur").toUpperCase()}`;
  const method = (tx) => (tx.provider === "paypal" ? "PayPal" : "Stripe");

  const accountName = user?.name || user?.email || "";
  const isAdmin = user?.role === "admin";
  const deleteAccount = async () => {
    if (isAdmin) return;
    if (delText !== accountName) return;
    setDeleting(true);
    try {
      await api.delete("/auth/me");
      toast.success(t("acct.deleteSuccess"));
      logout();
      nav("/login");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || t("acct.error"));
      setDeleting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b border-border sticky top-0 bg-background/90 backdrop-blur z-20">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2 font-heading font-extrabold text-xl"><Logo size={24} className="text-brand-yellow" /> {t("acct.title")}</div>
          <Button variant="ghost" onClick={() => nav("/dashboard")} className="gap-2" data-testid="settings-back"><ArrowLeft size={18} /> {t("admin.back")}</Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-10 space-y-8">
        <section className="bg-card border border-border rounded-xl p-6 space-y-4" data-testid="settings-account">
          <h2 className="font-heading font-bold text-lg">{t("acct.account")}</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>{t("acct.username")}</Label>
              {editingName ? (
                <div className="flex gap-2">
                  <Input value={nameInput} onChange={(e) => setNameInput(e.target.value)} autoFocus data-testid="settings-username-input" />
                  <Button size="icon" onClick={saveName} disabled={!nameInput.trim()} data-testid="settings-username-save"><Check size={18} /></Button>
                  <Button size="icon" variant="ghost" onClick={() => setEditingName(false)} data-testid="settings-username-cancel"><X size={18} /></Button>
                </div>
              ) : (
                <div className="relative">
                  <Input value={user?.name || ""} readOnly disabled className="pr-10" data-testid="settings-username" />
                  <button type="button" onClick={() => { setNameInput(user?.name || ""); setEditingName(true); }} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" title={t("acct.editName")} data-testid="settings-username-edit"><PencilSimple size={18} /></button>
                </div>
              )}
            </div>
            <div>
              <Label>{t("acct.email")}</Label>
              <Input value={user?.email || ""} readOnly disabled data-testid="settings-email" />
            </div>
          </div>
          <div className="flex items-center gap-2" data-testid="settings-token-credit">
            <Coins size={20} className="text-gold" />
            <span className="text-sm text-muted-foreground">{t("acct.tokenCredit")}:</span>
            <span className="font-semibold text-gold" data-testid="settings-token-balance">{user?.tokens_balance ?? 0}</span>
          </div>
        </section>

        <section className="bg-card border border-border rounded-xl p-6 space-y-4" data-testid="settings-password">
          <h2 className="font-heading font-bold text-lg">{t("acct.changePw")}</h2>
          <div className="max-w-xs">
            <Label>{t("acct.oldPw")}</Label>
            <div className="relative">
              <Input type={showOldPw ? "text" : "password"} value={oldPw} onChange={(e) => setOldPw(e.target.value)} className="pr-10" data-testid="settings-old-pw" />
              <button type="button" onClick={() => setShowOldPw((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" data-testid="settings-old-pw-eye">{showOldPw ? <EyeSlash size={18} /> : <Eye size={18} />}</button>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label>{t("acct.newPw")}</Label>
              <div className="relative">
                <Input type={showNewPw ? "text" : "password"} value={newPw} onChange={(e) => setNewPw(e.target.value)} className="pr-10" data-testid="settings-new-pw" />
                <button type="button" onClick={() => setShowNewPw((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" data-testid="settings-new-pw-eye">{showNewPw ? <EyeSlash size={18} /> : <Eye size={18} />}</button>
              </div>
            </div>
            <div>
              <Label>{t("acct.confirmPw")}</Label>
              <div className="relative">
                <Input type={showConfirmPw ? "text" : "password"} value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} className="pr-10" data-testid="settings-confirm-pw" />
                <button type="button" onClick={() => setShowConfirmPw((v) => !v)} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" data-testid="settings-confirm-pw-eye">{showConfirmPw ? <EyeSlash size={18} /> : <Eye size={18} />}</button>
              </div>
            </div>
          </div>
          {newPw && confirmPw && newPw !== confirmPw && <p className="text-xs text-hong" data-testid="settings-pw-mismatch">{t("acct.pwMismatch")}</p>}
          <Button onClick={changePw} disabled={!oldPw || !newPw || newPw !== confirmPw} data-testid="settings-change-pw-btn">{t("acct.changePw")}</Button>
        </section>

        <section className="bg-card border border-border rounded-xl p-6 space-y-4" data-testid="settings-antiphish">
          <div>
            <h2 className="font-heading font-bold text-lg">{t("acct.antiPhish")}</h2>
            <p className="text-base mt-1" data-testid="settings-current-antiphish">{savedAntiPhish || "-"}</p>
          </div>
          <p className="text-sm text-muted-foreground">{t("acct.antiPhishHint")}</p>
          <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
            <div className="flex-1">
              <Label>{t("acct.antiPhishEdit")}</Label>
              <Input value={antiPhish} onChange={(e) => setAntiPhish(e.target.value)} placeholder="es. Tigre-2026" data-testid="settings-antiphish-code" />
            </div>
            <Button onClick={saveAntiPhish} disabled={!antiPhish.trim()} data-testid="settings-save-antiphish-btn">{t("admin.save")}</Button>
          </div>
        </section>

        <section className="bg-card border border-border rounded-xl p-6" data-testid="settings-transactions">
          <h2 className="font-heading font-bold text-lg mb-4 flex items-center gap-2"><Coins size={20} className="text-gold" /> {t("acct.movements")}</h2>
          {txs.length === 0 ? (
            <p className="text-muted-foreground text-sm">{t("acct.noMovements")}</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-muted-foreground border-b border-border">
                    <th className="py-2 pr-4">{t("acct.date")}</th>
                    <th className="py-2 pr-4">{t("acct.tokens")}</th>
                    <th className="py-2 pr-4">{t("acct.amount")}</th>
                    <th className="py-2 pr-4">{t("acct.method")}</th>
                  </tr>
                </thead>
                <tbody>
                  {txs.map((tx, i) => (
                    <tr key={tx.created_at || i} className="border-b border-border/50" data-testid={`settings-tx-${i}`}>
                      <td className="py-2 pr-4">{new Date(tx.created_at).toLocaleString()}</td>
                      <td className="py-2 pr-4 text-gold font-semibold">+{tx.tokens}</td>
                      <td className="py-2 pr-4">{money(tx.amount, tx.currency)}</td>
                      <td className="py-2 pr-4">{method(tx)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <section className="bg-card border border-hong/40 rounded-xl p-6 space-y-4" data-testid="settings-danger">
          <h2 className="font-heading font-bold text-lg text-hong">{t("acct.dangerZone")}</h2>
          <p className="text-sm text-muted-foreground">{t("acct.deleteWarnShort")}</p>
          <Button variant="destructive" className="gap-2" disabled={isAdmin} onClick={() => { setDelText(""); setDelOpen(true); }} title={isAdmin ? t("acct.deleteAdminNote") : undefined} data-testid="delete-account-btn">
            <Trash size={18} /> {t("acct.deleteAccount")}
          </Button>
          {isAdmin && <p className="text-xs text-muted-foreground" data-testid="delete-admin-note">{t("acct.deleteAdminNote")}</p>}
        </section>
      </main>

      <Dialog open={delOpen} onOpenChange={setDelOpen}>
        <DialogContent className="bg-card border-border" data-testid="delete-account-dialog">
          <DialogHeader><DialogTitle className="text-hong">{t("acct.deleteTitle")}</DialogTitle></DialogHeader>
          <DialogDescription className="text-sm text-muted-foreground whitespace-pre-line">{t("acct.deleteWarn")}</DialogDescription>
          <button type="button" title={t("dash.clickToCopy")} data-testid="delete-account-name-hint"
            onClick={async () => { await copyToClipboard(accountName); toast.success(t("ring.copied")); }}
            className="w-full flex items-center justify-between gap-2 text-left font-monot text-sm bg-secondary border border-border rounded-md px-3 py-2 hover:border-hong transition-colors">
            <span className="truncate">{accountName}</span>
            <Copy size={16} className="shrink-0 text-muted-foreground" />
          </button>
          <Input autoFocus value={delText} onChange={(e) => setDelText(e.target.value)} placeholder={accountName} data-testid="delete-account-confirm-input" />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setDelOpen(false)} data-testid="delete-account-cancel">{t("dash.cancel")}</Button>
            <Button variant="destructive" disabled={deleting || delText !== accountName} onClick={deleteAccount} data-testid="delete-account-confirm-btn">
              {deleting ? "…" : t("acct.deleteCta")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
